import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { adminQuizService } from '../../shared/services';
import type { Quiz, QuizOpcao, QuizQuestao } from '../../shared/types';

const QUIZ_VAZIO: Quiz = {
  titulo: 'Avaliação final',
  descricao: '',
  notaMinima: 70,
  tentativasMaximas: 3,
  tempoLimiteMinutos: null,
  embaralharQuestoes: true,
  exigirAprovacao: true,
  questoes: [],
};

function novaQuestao(): QuizQuestao {
  return {
    enunciado: '',
    pontos: 1,
    opcoes: [
      { texto: '', correta: false },
      { texto: '', correta: false },
    ],
  };
}

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

export function AdminQuizPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [quiz, setQuiz] = useState<Quiz>(QUIZ_VAZIO);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { data, isError } = useQuery({
    queryKey: ['quiz-admin', id],
    queryFn: () => adminQuizService.get(id!),
    enabled: !!id,
    retry: false,
  });

  useEffect(() => {
    // Sincroniza o formulário com a avaliação carregada do servidor.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (data) setQuiz({ ...data, questoes: data.questoes ?? [] });
    if (isError) setQuiz(QUIZ_VAZIO);
  }, [data, isError]);

  const salvar = useMutation({
    mutationFn: () => adminQuizService.salvar(id!, quiz),
    onSuccess: (salvo) => {
      setQuiz(salvo);
      setMensagem('Avaliação salva.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['quiz-admin', id] });
      queryClient.invalidateQueries({ queryKey: ['quiz-curso', id] });
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível salvar a avaliação.')),
  });

  const remover = useMutation({
    mutationFn: () => adminQuizService.remover(id!),
    onSuccess: () => {
      setQuiz(QUIZ_VAZIO);
      setMensagem('Avaliação removida.');
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['quiz-admin', id] });
      queryClient.invalidateQueries({ queryKey: ['quiz-curso', id] });
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível remover a avaliação.')),
  });

  const atualizarQuestao = (indice: number, alteracao: Partial<QuizQuestao>) => {
    setQuiz((atual) => ({
      ...atual,
      questoes: atual.questoes.map((questao, i) =>
        i === indice ? { ...questao, ...alteracao } : questao,
      ),
    }));
  };

  const atualizarOpcao = (qi: number, oi: number, alteracao: Partial<QuizOpcao>) => {
    setQuiz((atual) => ({
      ...atual,
      questoes: atual.questoes.map((questao, i) =>
        i !== qi
          ? questao
          : {
              ...questao,
              opcoes: questao.opcoes.map((opcao, j) =>
                j === oi ? { ...opcao, ...alteracao } : opcao,
              ),
            },
      ),
    }));
  };

  const validar = (): string | null => {
    if (quiz.questoes.length === 0) return 'Adicione pelo menos uma questão.';
    for (const [indice, questao] of quiz.questoes.entries()) {
      if (!questao.enunciado.trim()) return `A questão ${indice + 1} está sem enunciado.`;
      if (questao.opcoes.length < 2) return `A questão ${indice + 1} precisa de pelo menos duas opções.`;
      if (questao.opcoes.some((opcao) => !opcao.texto.trim())) return `A questão ${indice + 1} tem opção sem texto.`;
      if (!questao.opcoes.some((opcao) => opcao.correta)) return `A questão ${indice + 1} precisa de uma opção correta.`;
    }
    return null;
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/cursos" className="inline-flex items-center gap-2 text-primary-600 mb-6">
          <ArrowLeft size={16} /> Voltar aos cursos
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Avaliação do curso</h1>
            <p className="text-gray-600 mt-1">
              Correção no servidor, nota mínima e limite de tentativas. A avaliação bloqueia a conclusão do curso.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Remover a avaliação deste curso?')) remover.mutate();
            }}
            className="text-red-600 hover:text-red-700 text-sm"
          >
            Remover
          </button>
        </div>

        {erro && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}
        {mensagem && <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{mensagem}</div>}

        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            const problema = validar();
            if (problema) {
              setErro(problema);
              setMensagem(null);
              return;
            }
            salvar.mutate();
          }}
        >
          <section className="card space-y-4">
            <div>
              <label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input
                id="titulo"
                value={quiz.titulo}
                onChange={(event) => setQuiz({ ...quiz, titulo: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>

            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea
                id="descricao"
                value={quiz.descricao ?? ''}
                onChange={(event) => setQuiz({ ...quiz, descricao: event.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="nota" className="block text-sm font-medium text-gray-700 mb-1">Nota mínima (%)</label>
                <input
                  id="nota"
                  type="number"
                  min={0}
                  max={100}
                  value={quiz.notaMinima}
                  onChange={(event) => setQuiz({ ...quiz, notaMinima: Number(event.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label htmlFor="tentativas" className="block text-sm font-medium text-gray-700 mb-1">Tentativas</label>
                <input
                  id="tentativas"
                  type="number"
                  min={1}
                  value={quiz.tentativasMaximas ?? ''}
                  onChange={(event) =>
                    setQuiz({ ...quiz, tentativasMaximas: event.target.value ? Number(event.target.value) : null })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Sem limite"
                />
              </div>
              <div>
                <label htmlFor="tempo" className="block text-sm font-medium text-gray-700 mb-1">Tempo (min)</label>
                <input
                  id="tempo"
                  type="number"
                  min={1}
                  value={quiz.tempoLimiteMinutos ?? ''}
                  onChange={(event) =>
                    setQuiz({ ...quiz, tempoLimiteMinutos: event.target.value ? Number(event.target.value) : null })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Sem limite"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-gray-700">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quiz.embaralharQuestoes}
                  onChange={(event) => setQuiz({ ...quiz, embaralharQuestoes: event.target.checked })}
                />
                Embaralhar questões
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={quiz.exigirAprovacao}
                  onChange={(event) => setQuiz({ ...quiz, exigirAprovacao: event.target.checked })}
                />
                Exigir aprovação para concluir o curso
              </label>
            </div>
          </section>

          <section className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Questões</h2>
              <button
                type="button"
                onClick={() => setQuiz({ ...quiz, questoes: [...quiz.questoes, novaQuestao()] })}
                className="btn-secondary flex items-center gap-1"
              >
                <Plus size={16} /> Questão
              </button>
            </div>

            {quiz.questoes.length === 0 && (
              <p className="text-sm text-gray-500">Nenhuma questão. Adicione a primeira.</p>
            )}

            <div className="space-y-6">
              {quiz.questoes.map((questao, qi) => (
                <div key={qi} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <span className="mt-2 text-sm font-semibold text-gray-500">{qi + 1}.</span>
                    <div className="flex-1">
                      <textarea
                        value={questao.enunciado}
                        onChange={(event) => atualizarQuestao(qi, { enunciado: event.target.value })}
                        placeholder="Enunciado da questão"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        rows={2}
                        required
                      />
                    </div>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={questao.pontos}
                      onChange={(event) => atualizarQuestao(qi, { pontos: Number(event.target.value) })}
                      className="w-20 border border-gray-300 rounded-lg px-2 py-2"
                      title="Pontos"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setQuiz({ ...quiz, questoes: quiz.questoes.filter((_, i) => i !== qi) })
                      }
                      className="mt-2 text-gray-400 hover:text-red-600"
                      title="Remover questão"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="mt-3 ml-6 space-y-2">
                    {questao.opcoes.map((opcao, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(opcao.correta)}
                          onChange={(event) => atualizarOpcao(qi, oi, { correta: event.target.checked })}
                          title="Opção correta"
                        />
                        <input
                          value={opcao.texto}
                          onChange={(event) => atualizarOpcao(qi, oi, { texto: event.target.value })}
                          placeholder={`Opção ${oi + 1}`}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          required
                        />
                        <button
                          type="button"
                          onClick={() =>
                            atualizarQuestao(qi, { opcoes: questao.opcoes.filter((_, j) => j !== oi) })
                          }
                          className="text-gray-400 hover:text-red-600"
                          title="Remover opção"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        atualizarQuestao(qi, { opcoes: [...questao.opcoes, { texto: '', correta: false }] })
                      }
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      + Adicionar opção
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <button
            type="submit"
            disabled={salvar.isPending}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={16} /> {salvar.isPending ? 'Salvando...' : 'Salvar avaliação'}
          </button>
        </form>
      </div>
    </div>
  );
}
